import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Typography
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { FormattedMessage } from 'react-intl';
import axiosConfig from 'src/Resources/axiosConfig';

const ALLOWED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf'
];
const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB

export interface UploadedDocument {
  id: number;
  originalName: string;
  contentType: string;
  sizeBytes: number;
  createdAt: string;
}

interface FileUploadButtonProps {
  scope: number; // 0 = TicketAttachment, 1 = InstallationDocument
  ticketId?: number;
  ticketCommentId?: number;
  installationId?: number;
  onUploaded?: (doc: UploadedDocument) => void;
  disabled?: boolean;
}

function FileUploadButton({
  scope,
  ticketId,
  ticketCommentId,
  installationId,
  onUploaded,
  disabled = false
}: FileUploadButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const validFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!ALLOWED_TYPES.includes(file.type)) {
        setError(`Invalid file type: ${file.name}`);
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        setError(`File too large: ${file.name} (max 25 MB)`);
        return;
      }
      validFiles.push(file);
    }

    setError('');
    setPendingFiles((prev) => [...prev, ...validFiles]);

    // Reset input so the same file can be selected again
    if (inputRef.current) inputRef.current.value = '';

    // Upload files sequentially to avoid race conditions
    uploadFilesSequentially(validFiles);
  };

  const uploadFilesSequentially = async (files: File[]) => {
    setUploading(true);
    for (const file of files) {
      setProgress(0);
      const formData = new FormData();
      formData.append('file', file);

      try {
        const res = await axiosConfig.post('/UploadDocument', formData, {
          params: { scope, ticketId, ticketCommentId, installationId },
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
          }
        });
        setPendingFiles((prev) => prev.filter((f) => f !== file));
        if (onUploaded) onUploaded(res.data);
      } catch (err: any) {
        const serverMsg = err?.response?.data || err?.message || 'Unknown error';
        setError(`Failed to upload ${file.name}: ${serverMsg}`);
        setPendingFiles((prev) => prev.filter((f) => f !== file));
      }
    }
    setUploading(false);
    setProgress(0);
  };

  return (
    <Box>
      <input
        ref={inputRef}
        type="file"
        accept={ALLOWED_TYPES.join(',')}
        multiple
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />
      <Button
        variant="outlined"
        size="small"
        startIcon={<AttachFileIcon />}
        onClick={() => inputRef.current?.click()}
        disabled={disabled || uploading}
      >
        <FormattedMessage id="attachFiles" defaultMessage="Attach Files" />
      </Button>

      {uploading && (
        <Box sx={{ mt: 1, width: '100%' }}>
          <LinearProgress variant="determinate" value={progress} />
        </Box>
      )}

      {pendingFiles.length > 0 && (
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {pendingFiles.map((f, i) => (
            <Chip key={i} label={f.name} size="small" variant="outlined" />
          ))}
        </Box>
      )}

      {error && (
        <Typography color="error" variant="caption" sx={{ mt: 0.5, display: 'block' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}

export default FileUploadButton;
